import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RefreshIcon, XIcon } from "@heroicons/react/solid";
import { getAmenitiesGrouped } from "@/_actions/amenitiesActions";
import { updateProperty, useProperty } from "@/_actions/propertiesActions";

const AmenitiesSchema = Yup.object().shape({
  amenities: Yup.array().of(Yup.number()),
});

export default function Amenities({ callBack, propertyId, goBack, action }) {
  const [categoriesData, setCategoriesData] = useState([]);
  const [uncategorizedAmenities, setUncategorizedAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { data } = useProperty(propertyId);

  const getInitialValues = () => {
    const initialAmenities = data?.amenities ? [...data.amenities] : [];
    return { amenities: initialAmenities };
  };

  const onSubmit = async (values) => {
    try {
      const cleanValues = {
        amenities: values.amenities,
      };

      const response = await updateProperty(propertyId, cleanValues);

      if (callBack) {
        callBack(response);
      }
    } catch (err) {
      console.error("Error updating amenities:", err);
    }
  };

  const getAllAmenities = () => {
    const allAmenities = [];
    categoriesData.forEach((category) => {
      allAmenities.push(...category.amenities);
    });
    allAmenities.push(...uncategorizedAmenities);
    return allAmenities;
  };

  const findAmenityCategory = (amenityId) => {
    for (const category of categoriesData) {
      if (category.amenities.find((a) => a.id === amenityId)) {
        return category.name;
      }
    }
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getAmenitiesGrouped();

        setCategoriesData(data.categories || []);
        setUncategorizedAmenities(data.uncategorized_amenities || []);
      } catch (err) {
        console.error("Error fetching amenities:", err);
        setError("Error al cargar las amenidades. Inténtalo de nuevo.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Amenidades</h2>
          <p className="text-gray-600">Cargando amenidades...</p>
        </div>
        <div className="flex items-center justify-center p-8">
          <RefreshIcon className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Cargando amenidades...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Amenidades</h2>
        </div>
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="grid grid-cols-2 gap-4 pt-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={goBack}
            className="w-full"
          >
            Regresar
          </Button>
          <Button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full bg-blue-800 hover:bg-blue-900"
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  if (categoriesData.length === 0 && uncategorizedAmenities.length === 0) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Amenidades</h2>
        </div>
        <Card className="w-full">
          <CardContent className="text-center py-8 text-gray-500">
            No hay amenidades disponibles
          </CardContent>
        </Card>
        <div className="grid grid-cols-2 gap-4 pt-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={goBack}
            className="w-full"
          >
            Regresar
          </Button>
          <Button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full bg-blue-800 hover:bg-blue-900"
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Amenidades</h2>
        <p className="text-gray-600">
          Selecciona las amenidades disponibles en la propiedad. Puedes elegir
          múltiples opciones por categoría.
        </p>
      </div>

      <Formik
        onSubmit={onSubmit}
        validationSchema={AmenitiesSchema}
        initialValues={getInitialValues()}
        enableReinitialize={true}
      >
        {({ values, isSubmitting, setFieldValue }) => {
          const selectedCount = values.amenities ? values.amenities.length : 0;
          const allAmenities = getAllAmenities();
          const selectedAmenities = allAmenities.filter(
            (amenity) =>
              values.amenities && values.amenities.includes(amenity.id)
          );

          const handleAmenityChange = (amenityId, checked) => {
            const currentValues = values.amenities || [];
            let newValues;

            if (checked) {
              newValues = [...currentValues, amenityId];
            } else {
              newValues = currentValues.filter((id) => id !== amenityId);
            }

            setFieldValue("amenities", newValues);
          };

          const removeAmenity = (amenityId) => {
            const currentValues = values.amenities || [];
            const newValues = currentValues.filter((id) => id !== amenityId);
            setFieldValue("amenities", newValues);
          };

          const renderAmenitiesGrid = (amenities) => (
            <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              {amenities.map((amenity) => {
                const isChecked =
                  values.amenities && values.amenities.includes(amenity.id);

                return (
                  <div
                    key={amenity.id}
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Checkbox
                      id={`amenity_${amenity.id}`}
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        handleAmenityChange(amenity.id, checked);
                      }}
                      className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <label
                      htmlFor={`amenity_${amenity.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                    >
                      {amenity.name}
                    </label>
                  </div>
                );
              })}
            </div>
          );

          return (
            <Form data-splitbee-event={`properties-${action}-amenities`}>
              <div className="space-y-3">
                {selectedCount > 0 && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="text-lg text-blue-800 flex items-center gap-2">
                        Amenidades seleccionadas
                        <Badge
                          variant="secondary"
                          className="bg-blue-100 text-blue-800"
                        >
                          {selectedCount}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="text-blue-600 -mb-2">
                        Revisa y edita las amenidades que has seleccionado
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-1">
                        {selectedAmenities.map((amenity) => {
                          const categoryName = findAmenityCategory(amenity.id);

                          return (
                            <div
                              key={`selected-${amenity.id}`}
                              className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-sm font-medium group hover:bg-blue-200 transition-colors"
                            >
                              {categoryName && (
                                <span className="text-xs text-blue-600 opacity-75">
                                  {categoryName}:
                                </span>
                              )}
                              <span>{amenity.name}</span>
                              <button
                                type="button"
                                onClick={() => removeAmenity(amenity.id)}
                                className="ml-px hover:bg-blue-300 rounded-full p-0.5 transition-colors opacity-0 group-hover:opacity-100"
                                title="Quitar amenidad"
                              >
                                <XIcon className="text-black w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {categoriesData.map((category) => {
                  const selectedInCategory = category.amenities.filter(
                    (a) => values.amenities && values.amenities.includes(a.id)
                  ).length;

                  return (
                    <Card key={category.id} className="w-full">
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          {category.name}
                          {selectedInCategory > 0 && (
                            <Badge variant="outline" className="ml-2">
                              {selectedInCategory} seleccionada
                              {selectedInCategory !== 1 ? "s" : ""}
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription>
                          Selecciona las amenidades de{" "}
                          {category.name.toLowerCase()} disponibles
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {renderAmenitiesGrid(category.amenities, category.name)}
                      </CardContent>
                    </Card>
                  );
                })}

                {uncategorizedAmenities.length > 0 && (
                  <Card className="w-full">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        Otras amenidades
                        {uncategorizedAmenities.filter(
                          (a) =>
                            values.amenities && values.amenities.includes(a.id)
                        ).length > 0 && (
                          <Badge variant="outline" className="ml-2">
                            {
                              uncategorizedAmenities.filter(
                                (a) =>
                                  values.amenities &&
                                  values.amenities.includes(a.id)
                              ).length
                            }{" "}
                            seleccionada
                            {uncategorizedAmenities.filter(
                              (a) =>
                                values.amenities &&
                                values.amenities.includes(a.id)
                            ).length !== 1
                              ? "s"
                              : ""}
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>
                        Amenidades adicionales disponibles
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {renderAmenitiesGrid(uncategorizedAmenities)}
                    </CardContent>
                  </Card>
                )}

                <Separator />

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={goBack}
                    data-splitbee-event={`properties-${action}-amenities-back`}
                    className="w-full"
                  >
                    Regresar
                  </Button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-800 hover:bg-blue-900"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <RefreshIcon className="mr-2 h-4 w-4 animate-spin" />
                        Procesando...
                      </div>
                    ) : (
                      "Continuar"
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
